/**
 * AdminEmulate — full "act as user" oversight overlay for platform admins.
 *
 * Given a target user, it opens a short-lived (30m) impersonation token from the backend
 * (/platform-admin/impersonate) and renders a consolidated, live read-out of that user's account —
 * bot status, config, account P&L, positions, recent trades, war-room activity — by calling the
 * ordinary user endpoints with the act-as token (the backend makes every endpoint operate on the
 * target). Admins can act as the user (start/stop their bot) with a per-action confirm. A persistent
 * banner + countdown makes it impossible to forget you're impersonating; the session auto-exits at
 * expiry. The real admin token is never touched — this component holds the act-as token in state and
 * uses it only for its own requests.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ShieldAlert, X, RefreshCw, Play, Square, DollarSign, Bot as BotIcon, Activity } from 'lucide-react';
import toast from 'react-hot-toast';

const money = (n) => (n == null || isNaN(n) ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const pct = (n) => (n == null || isNaN(n) ? '—' : `${(Number(n) * 100).toFixed(2)}%`);

export default function AdminEmulate({ targetUser, onExit }) {
    const [token, setToken] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [data, setData] = useState({});
    const exitedRef = useRef(false);

    const targetName = targetUser?.username || targetUser?.email || 'user';
    const targetId = targetUser?.id || targetUser?._id;

    const doExit = useCallback(() => {
        if (exitedRef.current) return;
        exitedRef.current = true;
        setToken(null);
        onExit && onExit();
    }, [onExit]);

    // Fetch through the act-as token.
    const af = useCallback((path, opts = {}) => fetch(path, {
        ...opts,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
    }), [token]);

    // 1) Start the impersonation session (mint the act-as token).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch('/api/platform-admin/impersonate', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: targetId }),
                });
                const j = await r.json();
                if (!r.ok) throw new Error(j.error || 'Failed to start emulation');
                if (cancelled) return;
                setToken(j.token);
                setExpiresAt(Date.now() + (j.expiresInSec || 1800) * 1000);
            } catch (e) {
                if (!cancelled) { setErr(e.message); setLoading(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [targetId]);

    // 2) Load the consolidated view once we have a token.
    const loadAll = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        const grab = (p) => af(p).then(r => r.json()).catch(() => null);
        const [status, config, account, positions, history, decisions] = await Promise.all([
            grab('/api/bot/status'),
            grab('/api/bot/config'),
            grab('/api/alpaca/account'),
            grab('/api/alpaca/positions'),
            grab('/api/bot/history?limit=8'),
            grab('/api/warroom/decisions?limit=8'),
        ]);
        setData({ status, config, account, positions, history, decisions });
        setLoading(false);
    }, [token, af]);

    useEffect(() => { if (token) loadAll(); }, [token, loadAll]);

    // 3) Countdown + auto-exit at expiry.
    useEffect(() => {
        if (!expiresAt) return;
        const t = setInterval(() => {
            const s = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
            setSecondsLeft(s);
            if (s <= 0) { toast('Emulation session expired'); doExit(); }
        }, 1000);
        return () => clearInterval(t);
    }, [expiresAt, doExit]);

    // Confirmed "act as them" write action.
    const act = async (label, path) => {
        if (!window.confirm(`You are about to ${label} AS ${targetName}. This acts on their real account. Continue?`)) return;
        setBusy(true);
        try {
            const r = await af(path, { method: 'POST' });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || 'Action failed');
            toast.success(`${label} — done (as ${targetName})`);
            await loadAll();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const mmss = secondsLeft == null ? '' : `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;
    const cfg = data.config?.config || data.config || {};
    const acct = data.account || {};
    const positions = Array.isArray(data.positions) ? data.positions : (data.positions?.positions || []);
    const trades = Array.isArray(data.history) ? data.history : (data.history?.trades || data.history?.history || []);
    const decisions = Array.isArray(data.decisions) ? data.decisions : (data.decisions?.decisions || []);
    const botRunning = data.status?.running ?? data.status?.isRunning;

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 overflow-y-auto">
            <div className="min-h-full">
                {/* Persistent banner */}
                <div className="sticky top-0 z-10 bg-red-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
                    <div className="flex items-center gap-2 font-semibold">
                        <ShieldAlert size={20} />
                        <span>EMULATING <span className="underline">{targetName}</span> — every action is logged under your admin identity</span>
                    </div>
                    <div className="flex items-center gap-4">
                        {mmss && <span className="text-sm tabular-nums bg-red-800/60 px-2 py-1 rounded">expires in {mmss}</span>}
                        <button onClick={doExit} className="flex items-center gap-1 bg-white text-red-700 font-semibold px-3 py-1.5 rounded hover:bg-red-50">
                            <X size={16} /> Exit emulation
                        </button>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto p-4 md:p-6">
                    {err ? (
                        <div className="bg-red-900/40 border border-red-500 text-red-200 rounded-lg p-4">
                            Could not start emulation: {err}
                        </div>
                    ) : loading && !data.status ? (
                        <div className="text-text-muted flex items-center gap-2 p-8"><RefreshCw className="animate-spin" size={18} /> Loading {targetName}'s account…</div>
                    ) : (
                        <div className="space-y-5">
                            {/* Action bar */}
                            <div className="flex flex-wrap items-center gap-3">
                                <h2 className="text-xl font-bold text-text mr-2">Account: {targetName}</h2>
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${botRunning ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                    Bot {botRunning ? 'RUNNING' : 'stopped'}
                                </span>
                                <div className="flex-1" />
                                <button disabled={busy} onClick={() => act("start the bot", '/api/bot/start')}
                                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm">
                                    <Play size={15} /> Start bot
                                </button>
                                <button disabled={busy} onClick={() => act("stop the bot", '/api/bot/stop')}
                                    className="flex items-center gap-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white px-3 py-1.5 rounded text-sm">
                                    <Square size={15} /> Stop bot
                                </button>
                                <button disabled={busy} onClick={loadAll}
                                    className="flex items-center gap-1 bg-surface-2 hover:bg-surface-3 text-text px-3 py-1.5 rounded text-sm border border-border">
                                    <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
                                </button>
                            </div>

                            {/* Top stat row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Stat icon={<DollarSign size={16} />} label="Equity" value={money(acct.portfolioValue ?? acct.equity)} />
                                <Stat icon={<DollarSign size={16} />} label="Buying power" value={money(acct.buyingPower)} />
                                <Stat icon={<Activity size={16} />} label="Open positions" value={positions.length} />
                                <Stat icon={<BotIcon size={16} />} label="Strategies" value={(cfg.strategies || []).join(', ') || '—'} />
                            </div>

                            <div className="grid md:grid-cols-2 gap-5">
                                {/* Bot config */}
                                <Panel title="Bot configuration">
                                    <ConfigRow k="Risk profile" v={cfg.cryptoConfig?.riskProfile || cfg.riskProfile || '—'} />
                                    <ConfigRow k="Max positions" v={cfg.maxPositions} />
                                    <ConfigRow k="Min AI score" v={cfg.minAiScore} />
                                    <ConfigRow k="Brain entry gate" v={String(cfg.brainEntryGate ?? '—')} />
                                    <ConfigRow k="Brain runs/day" v={cfg.brainMaxRunsPerDay} />
                                    <ConfigRow k="Crypto enabled" v={String(cfg.cryptoEnabled ?? '—')} />
                                    <ConfigRow k="Daily loss cap" v={cfg.maxDailyLoss != null ? `${cfg.maxDailyLoss}%` : '—'} />
                                </Panel>

                                {/* Positions */}
                                <Panel title={`Positions (${positions.length})`}>
                                    {positions.length === 0 ? <Empty>No open positions</Empty> :
                                        positions.slice(0, 10).map((p, i) => (
                                            <div key={i} className="flex items-center justify-between py-1 text-sm border-b border-border/50 last:border-0">
                                                <span className="font-medium text-text">{p.symbol}</span>
                                                <span className="text-text-muted">{Number(p.qty).toLocaleString(undefined, { maximumFractionDigits: 4 })}</span>
                                                <span className="text-text-muted">{money(p.marketValue)}</span>
                                                <span className={Number(p.pnlPercent) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{p.pnlPercent != null && !isNaN(p.pnlPercent) ? `${Number(p.pnlPercent).toFixed(2)}%` : '—'}</span>
                                            </div>
                                        ))}
                                </Panel>

                                {/* Recent trades */}
                                <Panel title="Recent trades">
                                    {trades.length === 0 ? <Empty>No recent trades</Empty> :
                                        trades.slice(0, 8).map((t, i) => (
                                            <div key={i} className="flex items-center justify-between py-1 text-sm border-b border-border/50 last:border-0">
                                                <span className="text-text-muted">{(t.timestamp || t.date || '').slice(5, 16).replace('T', ' ')}</span>
                                                <span className="font-medium text-text">{t.symbol}</span>
                                                <span className="text-text-muted">{t.side || t.type}</span>
                                                <span className={Number(t.pnl) >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{t.pnl != null ? money(t.pnl) : '—'}</span>
                                            </div>
                                        ))}
                                </Panel>

                                {/* War-room activity */}
                                <Panel title="War-room (recent verdicts)">
                                    {decisions.length === 0 ? <Empty>No recent war-room decisions</Empty> :
                                        decisions.slice(0, 8).map((d, i) => (
                                            <div key={i} className="flex items-center justify-between py-1 text-sm border-b border-border/50 last:border-0">
                                                <span className="text-text-muted">{(d.ts || '').slice(5, 16).replace('T', ' ')}</span>
                                                <span className="font-medium text-text">{d.ticker}</span>
                                                <span className="text-text-muted">{d.brain?.decision || '—'}</span>
                                                <span className={d.final_action === 'BUY' ? 'text-emerald-400' : d.final_action === 'AVOID' ? 'text-rose-400' : 'text-text-muted'}>{d.final_action}</span>
                                            </div>
                                        ))}
                                </Panel>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const Stat = ({ icon, label, value }) => (
    <div className="bg-surface-2 border border-border rounded-lg p-3">
        <div className="flex items-center gap-1 text-text-muted text-xs mb-1">{icon}{label}</div>
        <div className="text-lg font-bold text-text truncate">{value}</div>
    </div>
);
const Panel = ({ title, children }) => (
    <div className="bg-surface-2 border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-text mb-2">{title}</h3>
        <div>{children}</div>
    </div>
);
const ConfigRow = ({ k, v }) => (
    <div className="flex items-center justify-between py-1 text-sm border-b border-border/50 last:border-0">
        <span className="text-text-muted">{k}</span>
        <span className="font-medium text-text">{v == null || v === '' ? '—' : v}</span>
    </div>
);
const Empty = ({ children }) => <div className="text-text-muted text-sm py-2">{children}</div>;
